import React from 'react';
import { Loader2, AlertCircle, CheckCircle } from 'lucide-react';
import PRICING from '../config/pricing';

const ProcessingCTA = ({
  selectedPhotos,
  freeTrialStatus,
  selectedStyle,
  descriptionSentences,
  settingUpBatch,
  loading,
  loadingPreferences,
  loadingFreeTrial,
  handleStartProcessing
}) => {
  return (
    <>
      {/* Free Trial Warning */}
      {freeTrialStatus && freeTrialStatus.isEligible && selectedPhotos.size > freeTrialStatus.remainingPhotos && (
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-4">
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0">
              <svg className="w-5 h-5 text-orange-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <div className="flex-1">
              <p className="text-sm text-orange-800">
                <span className="font-medium">Payment Required:</span> You've selected {selectedPhotos.size} photos, 
                but you have only {freeTrialStatus.remainingPhotos} free photos remaining.
              </p>
              <p className="text-xs text-orange-600 mt-1">
                {freeTrialStatus.remainingPhotos > 0 ? (
                  <>The first {freeTrialStatus.remainingPhotos} photos will be free, and you'll pay {PRICING.formatPrice(PRICING.calculateAmountWithFreeTrial(selectedPhotos.size, freeTrialStatus.remainingPhotos))} for the remaining {selectedPhotos.size - freeTrialStatus.remainingPhotos} photos.</>
                ) : (
                  <>You'll pay {PRICING.formatPrice(selectedPhotos.size * PRICING.baseRatePerPhoto)} for all {selectedPhotos.size} photos ({PRICING.getDisplayRate()} per photo).</>
                )}
              </p>
            </div>
          </div>
        </div>
      )}
        
      {/* Enhanced CTA Section */}
      <div className="bg-gradient-to-r from-green-400 to-blue-500 rounded-xl p-8 text-white text-center">
        {selectedPhotos.size === 0 ? (
          <div>
            <h3 className="text-2xl font-bold mb-3">Almost Ready!</h3>
            <p className="text-white text-opacity-90 mb-6">Select some photos above to see the magic happen</p>
            <button
              disabled={true}
              className="inline-flex items-center px-10 py-4 bg-white bg-opacity-20 text-white font-bold text-xl rounded-full cursor-not-allowed"
            >
              <AlertCircle className="mr-3 h-6 w-6" />
              Select Photos to Continue
            </button>
          </div>
        ) : (
          <div>
            <h3 className="text-2xl font-bold mb-2">✨ Ready to Transform Your Photo Titles, Descriptions, and Tags!</h3>
            <p className="text-white text-opacity-90 mb-2">
              Generate rich metadata for {selectedPhotos.size} photo{selectedPhotos.size !== 1 ? 's' : ''}
            </p>
            
            {/* Cost/Free Indicator */}
            <div className="mb-6">
              {freeTrialStatus && freeTrialStatus.isEligible && selectedPhotos.size <= freeTrialStatus.remainingPhotos ? (
                <div className="inline-flex items-center px-4 py-2 bg-green-100 text-green-800 rounded-full font-bold">
                  <CheckCircle className="w-5 h-5 mr-2" />
                  Completely FREE with your trial
                </div>
              ) : freeTrialStatus && freeTrialStatus.isEligible ? (
                <div className="inline-flex items-center px-4 py-2 bg-orange-100 text-orange-800 rounded-full font-bold">
                  <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M4 4a2 2 0 00-2 2v1h16V6a2 2 0 00-2-2H4zM18 9H2v5a2 2 0 002 2h12a2 2 0 002-2V9zM4 13a1 1 0 011-1h1a1 1 0 110 2H5a1 1 0 01-1-1zm5-1a1 1 0 100 2h1a1 1 0 100-2H9z" />
                  </svg>
                  {freeTrialStatus.remainingPhotos > 0 ? (
                    `${freeTrialStatus.remainingPhotos} free + ${PRICING.formatPrice(PRICING.calculateAmountWithFreeTrial(selectedPhotos.size, freeTrialStatus.remainingPhotos))} paid`
                  ) : (
                    `${PRICING.formatPrice(selectedPhotos.size * PRICING.baseRatePerPhoto)} total`
                  )}
                </div>
              ) : (
                <div className="inline-flex items-center px-4 py-2 bg-blue-100 text-blue-800 rounded-full font-bold">
                  <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M4 4a2 2 0 00-2 2v1h16V6a2 2 0 00-2-2H4zM18 9H2v5a2 2 0 002 2h12a2 2 0 002-2V9zM4 13a1 1 0 011-1h1a1 1 0 110 2H5a1 1 0 01-1-1zm5-1a1 1 0 100 2h1a1 1 0 100-2H9z" />
                  </svg>
                  {PRICING.getDisplayRate()} per photo
                </div>
              )}
            </div>
            
            {/* Main CTA Button */}
            <div className="space-y-3">

              
              <button
                onClick={handleStartProcessing}
                disabled={loading || loadingPreferences || loadingFreeTrial || settingUpBatch || selectedPhotos.size === 0}
                className="inline-flex items-center px-10 py-4 bg-white text-green-600 font-bold text-2xl rounded-full hover:bg-green-50 hover:scale-110 transition-all duration-200 shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {settingUpBatch ? (
                  <>
                    <Loader2 className="mr-3 h-7 w-7 animate-spin" />
                    Setting Up...
                  </>
                ) : (
                  <>
                    ✨ Generate My Photo Metadata
                  </>
                )}
              </button>
              
              <p className="text-white text-opacity-80 text-sm">
                Using {selectedStyle === 'descriptive' ? 'factual' : selectedStyle === 'artistic' ? 'storytelling' : 'playful'} style 
                • {descriptionSentences} sentence{descriptionSentences !== 1 ? 's' : ''} each
              </p>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default ProcessingCTA;
